/**
 * API keys, kept in `localStorage`.
 *
 * The workbench calls redesign backends straight from the page, so the key has
 * to live in the browser. For an internal tool used by a few people that is a
 * reasonable trade - it avoids running and deploying a proxy - but it is a real
 * trade and worth stating plainly: any script running on this origin can read
 * the key, and it travels in a request the user can inspect. Use a key scoped to
 * this tool, and rotate it rather than sharing one with production.
 *
 * Keys are namespaced per target so several backends can be configured at once.
 */

const PREFIX = 'design-ai:credential:';

export function loadCredential(targetId: string): string {
  if (typeof localStorage === 'undefined') return '';
  return localStorage.getItem(PREFIX + targetId) ?? '';
}

export function saveCredential(targetId: string, value: string): void {
  if (typeof localStorage === 'undefined') return;
  if (value) localStorage.setItem(PREFIX + targetId, value);
  else localStorage.removeItem(PREFIX + targetId);
}

export function clearCredential(targetId: string): void {
  saveCredential(targetId, '');
}

/** Target ids that currently have a key stored. */
export function storedCredentialIds(): string[] {
  if (typeof localStorage === 'undefined') return [];
  const ids: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key?.startsWith(PREFIX)) ids.push(key.slice(PREFIX.length));
  }
  return ids;
}

/** Builds the auth header pair, or nothing when no key is configured. */
export function authHeaders(
  apiKey: string | undefined,
  header = 'Authorization',
  prefix = 'Bearer ',
): Record<string, string> {
  if (!apiKey) return {};
  return { [header]: `${prefix}${apiKey}` };
}
